import { useEffect, useMemo, useState } from "react";
import type { FormField } from "../../components/AdminDataModal";
import type { Column } from "../../components/DataTable";
import { useMessageModal } from "../../contexts/MessageModalContext";
import { useFormat } from "../../../context/FormatContext";
import DataTable from "../../components/DataTable";
import AdminListToolkit from "../../components/listing/AdminListToolkit";
import AdminDataModal from "../../components/AdminDataModal";

export default function AdminPromotions() {
  const BASE = import.meta.env.VITE_LYRAZONE_BASE_URL;
  const { showModal } = useMessageModal();
  const { formatDateOnly } = useFormat();

  const [data, setData] = useState<any[]>([]);
  const [selectedRow, setSelectedRow] = useState<any>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit" | "view">("add");
  const [activeRow, setActiveRow] = useState<any>(null);

  const formatCurrency = (value: any) => {
    const amount = Number(value || 0);

    return amount.toLocaleString("en-MY", {
      style: "currency",
      currency: "MYR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatDateForInput = (value: any) => {
    if (!value) return "";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";

    return date.toISOString().split("T")[0];
  };

  const normalizePromotionPayload = (formData: any) => {
    return {
      ...formData,
      discount_value: String(formData.discount_value || "").replace(/,/g, ""),
      min_cart_amount: String(formData.min_cart_amount || "0").replace(
        /,/g,
        "",
      ),
      min_total_spent: String(formData.min_total_spent || "0").replace(
        /,/g,
        "",
      ),
      max_discount_amount:
        formData.max_discount_amount === "" ||
        formData.max_discount_amount === null ||
        formData.max_discount_amount === undefined
          ? null
          : String(formData.max_discount_amount).replace(/,/g, ""),
      min_cart_quantity: Number(formData.min_cart_quantity || 0),
      min_orders_count: Number(formData.min_orders_count || 0),
      usage_limit:
        formData.usage_limit === "" ||
        formData.usage_limit === null ||
        formData.usage_limit === undefined
          ? null
          : Number(formData.usage_limit),
      per_user_limit:
        formData.per_user_limit === "" ||
        formData.per_user_limit === null ||
        formData.per_user_limit === undefined
          ? null
          : Number(formData.per_user_limit),
      start_date: formData.start_date || null,
      end_date: formData.end_date || null,
    };
  };

  const fetchPromotions = async () => {
    try {
      const res = await fetch(`${BASE}/admin/promotions`, {
        credentials: "include",
      });

      const result = await res.json();
      const freshData = result.data || [];

      if (result.success) {
        setData(freshData);

        setSelectedRow((prev: any) =>
          prev
            ? freshData.find((item: any) => item.id === prev.id) || null
            : null,
        );

        setActiveRow((prev: any) =>
          prev
            ? freshData.find((item: any) => item.id === prev.id) || null
            : null,
        );
      }
    } catch (error) {
      console.error("Fetch Promotions Error:", error);
    }
  };

  useEffect(() => {
    fetchPromotions();
  }, []);

  const handleSubmit = async (formData: any) => {
    try {
      const method = modalMode === "add" ? "POST" : "PUT";

      const url =
        modalMode === "add"
          ? `${BASE}/admin/promotions`
          : `${BASE}/admin/promotions/${activeRow.id}`;

      const payload = normalizePromotionPayload(formData);

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const result = await res.json();

      if (result.success) {
        fetchPromotions();
        setModalOpen(false);
        setActiveRow(null);

        showModal({
          type: "success",
          title: "Success",
          message:
            modalMode === "add"
              ? "Promotion added successfully."
              : "Promotion updated successfully.",
        });
      } else {
        showModal({
          type: "error",
          title: "Error",
          message: result.message || "Unable to save promotion.",
        });
      }
    } catch (error) {
      console.error("Submit Promotion Error:", error);

      showModal({
        type: "error",
        title: "Error",
        message: "Something went wrong while saving promotion.",
      });
    }
  };

  const handleDelete = (row: any) => {
    showModal({
      type: "confirm",
      title: "Delete Promotion",
      message: "Are you sure you want to delete this promotion?",
      confirmText: "Delete",
      onConfirm: async () => {
        try {
          const res = await fetch(`${BASE}/admin/promotions/${row.id}`, {
            method: "DELETE",
            credentials: "include",
          });

          const result = await res.json();

          if (result.success) {
            fetchPromotions();
            setSelectedRow(null);

            showModal({
              type: "success",
              title: "Deleted",
              message: "Promotion deleted successfully.",
            });
          } else {
            showModal({
              type: "error",
              title: "Error",
              message: result.message || "Unable to delete promotion.",
            });
          }
        } catch (error) {
          console.error("Delete Promotion Error:", error);

          showModal({
            type: "error",
            title: "Error",
            message: "Something went wrong while deleting promotion.",
          });
        }
      },
    });
  };

  const promotionFields: FormField[] = useMemo(
    () => [
      { key: "id", label: "ID", colSpan: 2 },
      { key: "title", label: "Promotion Title", colSpan: 2 },
      { key: "code", label: "Promotion Code", colSpan: 2 },
      {
        key: "description",
        label: "Description",
        type: "textarea",
        colSpan: 2,
      },
      {
        key: "discount_type",
        label: "Discount Type",
        type: "select",
        options: [
          { label: "Fixed Amount", value: "fixed" },
          { label: "Percentage", value: "percent" },
        ],
        colSpan: 2,
      },
      {
        key: "discount_value",
        label: "Discount Value",
        type: "currency",
        colSpan: 2,
      },
      {
        key: "apply_type",
        label: "Apply Type",
        type: "select",
        options: [
          { label: "Manual Code", value: "manual_code" },
          { label: "Auto Apply", value: "auto" },
        ],
        colSpan: 2,
      },
      {
        key: "min_cart_amount",
        label: "Minimum Cart Amount",
        type: "currency",
        colSpan: 2,
      },
      {
        key: "min_cart_quantity",
        label: "Minimum Cart Quantity",
        type: "number",
        colSpan: 2,
      },
      {
        key: "min_orders_count",
        label: "Minimum Orders Count",
        type: "number",
        colSpan: 2,
      },
      {
        key: "min_total_spent",
        label: "Minimum Customer Total Spent",
        type: "currency",
        colSpan: 2,
      },
      {
        key: "max_discount_amount",
        label: "Maximum Discount Amount",
        type: "currency",
        colSpan: 2,
      },
      {
        key: "usage_limit",
        label: "Total Usage Limit",
        type: "number",
        colSpan: 2,
      },
      {
        key: "per_user_limit",
        label: "Per User Limit",
        type: "number",
        colSpan: 2,
      },
      {
        key: "start_date",
        label: "Start Date",
        type: "date",
        colSpan: 2,
      },
      {
        key: "end_date",
        label: "End Date",
        type: "date",
        colSpan: 2,
      },
      {
        key: "status",
        label: "Status",
        type: "select",
        options: [
          { label: "Active", value: "active" },
          { label: "Inactive", value: "inactive" },
        ],
        colSpan: 2,
      },
    ],
    [],
  );

  const columns: Column[] = [
    { key: "id", label: "ID" },
    { key: "title", label: "Title" },
    { key: "code", label: "Code" },
    {
      key: "discount_type",
      label: "Type",
      render: (row: any) =>
        row.discount_type === "percent" ? "Percentage" : "Fixed Amount",
    },
    {
      key: "discount_value",
      label: "Discount",
      render: (row: any) =>
        row.discount_type === "percent"
          ? `${Number(row.discount_value || 0)}%`
          : formatCurrency(row.discount_value),
    },
    {
      key: "apply_type",
      label: "Apply Type",
      render: (row: any) =>
        row.apply_type === "auto" ? "Auto Apply" : "Manual Code",
    },
    {
      key: "min_cart_amount",
      label: "Min Cart",
      render: (row: any) => formatCurrency(row.min_cart_amount),
    },
    {
      key: "min_cart_quantity",
      label: "Min Qty",
      render: (row: any) => row.min_cart_quantity || 0,
    },
    {
      key: "start_date",
      label: "Start Date",
      render: (row: any) => formatDateOnly(row.start_date),
    },
    {
      key: "end_date",
      label: "End Date",
      render: (row: any) => formatDateOnly(row.end_date),
    },
    {
      key: "status",
      label: "Status",
      render: (row: any) => (
        <span
          className={row.status === "active" ? "badge-success" : "badge-danger"}
        >
          {row.status?.charAt(0).toUpperCase() + row.status?.slice(1)}
        </span>
      ),
    },
  ];

  return (
    <div>
      <AdminListToolkit
        title="Promotions"
        columns={columns}
        data={data}
        selectedRow={selectedRow}
        onAdd={() => {
          const newRow = {
            id: "",
            title: "",
            code: "",
            description: "",
            discount_type: "fixed",
            discount_value: "",
            apply_type: "manual_code",
            min_cart_amount: "0",
            min_cart_quantity: 0,
            min_orders_count: 0,
            min_total_spent: "0",
            max_discount_amount: "",
            usage_limit: "",
            per_user_limit: "",
            start_date: "",
            end_date: "",
            status: "active",
          };

          setModalMode("add");
          setActiveRow(newRow);
          setModalOpen(true);
        }}
        onView={(row) => {
          setModalMode("view");
          setActiveRow({
            ...row,
            start_date: formatDateForInput(row.start_date),
            end_date: formatDateForInput(row.end_date),
          });
          setModalOpen(true);
        }}
        onEdit={(row) => {
          setModalMode("edit");
          setActiveRow({
            ...row,
            start_date: formatDateForInput(row.start_date),
            end_date: formatDateForInput(row.end_date),
          });
          setModalOpen(true);
        }}
        onSoftDelete={handleDelete}
        enableExport={false}
      >
        {(filteredData) => (
          <DataTable
            columns={columns}
            data={filteredData}
            selectedRowId={selectedRow?.id}
            onRowSelect={setSelectedRow}
          />
        )}
      </AdminListToolkit>

      <AdminDataModal
        isOpen={modalOpen}
        mode={modalMode}
        title="Promotion"
        fields={promotionFields}
        rowData={activeRow}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
        size="xl"
      />
    </div>
  );
}
